import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, List, Modal, Popover, Radio, Space, Spin, Tooltip, Typography, message } from 'antd';
import { EnvironmentOutlined, ExclamationCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  OrphanResolveAction,
  OrphanedAppointment,
  ScheduleConflictGroup,
  ScheduleConflictJob,
  ScheduleConflictJobContext,
  getScheduleConflicts,
  getTimeSlotsForDate,
  onScheduleConflictsUpdated,
  resolveOrphanAppointment,
  updateDispatchAppt,
} from '@/services/app/tenant/dispatch/dispatch';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { history, useAccess } from '@umijs/max';
import TimeSlotModal from '@/pages/tenant/dispatch/DispatchDashboard/components/TimeSlotModal';
import AppointmentRemovalReasonPicker from '@/components/AppointmentRemovalReasonPicker';
import { isRemovalPayloadComplete, type AppointmentRemovalPayload } from '@/constants/appointmentRemovalReasons';
import { formatTimeAmPm } from '@/utils/timeSlotUtils';
import styles from './index.less';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  customerName: string;
  dispatchId: number;
  status?: string;
}

/**
 * A pending TimeSlotModal session. Used for overlap-pair resolution (move
 * one of the overlapping jobs to a free slot) and for the orphan
 * "assign to current technician" flow (orphanAppointmentId set — the save
 * goes through the row-safe resolveOrphanAppointment endpoint).
 */
interface ResolveTarget {
  jobId: number;
  userId: number;
  appointmentDate: string;
  currentSlot: { startTime?: string; endTime?: string };
  slots: TimeSlot[];
  orphanAppointmentId?: number;
}

/**
 * Job identity lines shared by overlap rows, orphan rows and the orphan
 * chooser: address, type/trade, current status and a customer phone. This is
 * what lets the dispatcher pick WHICH overlapping job to move (and call the
 * customer if needed) straight from the bell instead of opening each job.
 */
const JobContextLines: React.FC<{ job: ScheduleConflictJobContext }> = ({ job }) => {
  const kind = [job.dispatch_type, job.trade].filter(Boolean).join(' · ');
  const meta = [kind, job.status_label].filter(Boolean).join(' · ');
  if (!job.address && !meta && !job.phone) return null;
  return (
    <>
      {job.address && (
        <div>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {job.address}
        </div>
      )}
      {(meta || job.phone) && (
        <div>
          {meta}
          {job.phone && (
            <span style={{ marginLeft: meta ? 8 : 0, whiteSpace: 'nowrap' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {job.phone}
            </span>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Topbar badge listing schedule problems needing dispatcher attention:
 *  - overlapping appointments (same technician, same date), and
 *  - orphaned appointments (the appointment's technician is no longer
 *    assigned to the job — e.g. a half-completed reassignment).
 * Overlaps resolve via the TimeSlotModal. Orphans open an action chooser:
 * move the slot to the current technician, re-add the removed technician
 * (primary or secondary — dispatcher's decision), or release the stale slot.
 */
const ConflictBell: React.FC = () => {
  const access = useAccess();
  // "Release the stale slot" soft-deletes the appointment — gated by
  // appointment.remove (the backend refuses it with a 403 as well).
  const canRemoveAppointment = !!access.canRemoveAppointment;
  const [open, setOpen]                     = useState(false);
  const [groups, setGroups]                 = useState<ScheduleConflictGroup[]>([]);
  const [orphans, setOrphans]               = useState<OrphanedAppointment[]>([]);
  const [loading, setLoading]               = useState(false);
  const [resolveTarget, setResolveTarget]   = useState<ResolveTarget | null>(null);
  const [resolvingJobId, setResolvingJobId] = useState<number | null>(null);
  const [orphanChooser, setOrphanChooser]   = useState<OrphanedAppointment | null>(null);
  const [orphanAction, setOrphanAction]     = useState<OrphanResolveAction>('release');
  // "Release" is a real removal — the office requires a reason (backend 400s without one).
  const [orphanRemoval, setOrphanRemoval]   = useState<AppointmentRemovalPayload>({});
  const [orphanSubmitting, setOrphanSubmitting] = useState(false);

  const conflictCount = groups.reduce((sum, g) => sum + g.jobs.length, 0) + orphans.length;

  // ----------------------------------------------------------------- fetch
  const fetchConflicts = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getScheduleConflicts();
      setGroups(res?.success && Array.isArray(res.data) ? res.data : []);
      setOrphans(res?.success && Array.isArray(res.orphans) ? res.orphans : []);
    } catch {
      // silently ignore – don't spam the user with errors
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  // No poll interval — a "schedule-conflicts-updated" WS push (see
  // onScheduleConflictsUpdated) fires whenever an appointment moves, a
  // technician is (re)assigned, or a job is canceled/un-canceled — the only
  // things that can create or resolve a conflict — and this re-fetches over
  // REST since the push is signal-only. The socket-reconnect signal covers
  // whatever was missed while disconnected.
  useEffect(() => {
    fetchConflicts();
    ensureNotificationSocketConnected();
    return onScheduleConflictsUpdated(() => fetchConflicts());
  }, [fetchConflicts]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) fetchConflicts(true);
  }, [fetchConflicts]);

  // ----------------------------------------------------------------- overlap resolve
  const loadDaySlots = useCallback(async (userId: number, appointmentDate: string): Promise<TimeSlot[]> => {
    const response = await getTimeSlotsForDate({
      user_id: String(userId),
      appointment_date: appointmentDate,
    });
    return (response?.success && response.data ? response.data : []).map((slot: any) => ({
      id: slot.id?.toString() || '',
      startTime: slot.time_from || '',
      endTime: slot.time_to || '',
      customerName: slot.customer_name || 'Unknown',
      dispatchId: Number(slot.job_id ?? slot.id) || 0,
      status: slot.status || '',
    }));
  }, []);

  const openResolveModal = useCallback(async (
    jobId: number,
    userId: number,
    appointmentDate: string,
    currentSlot: { startTime?: string; endTime?: string },
    orphanAppointmentId?: number,
  ) => {
    setResolvingJobId(jobId);
    try {
      const slots = await loadDaySlots(userId, appointmentDate);
      setOpen(false);
      setResolveTarget({ jobId, userId, appointmentDate, currentSlot, slots, orphanAppointmentId });
    } catch {
      message.error('Failed to load the technician\'s schedule');
    } finally {
      setResolvingJobId(null);
    }
  }, [loadDaySlots]);

  const handleResolve = useCallback((group: ScheduleConflictGroup, job: ScheduleConflictJob) => {
    openResolveModal(job.job_id, group.user_id, group.appointment_date, {
      startTime: job.time_from,
      endTime: job.time_to,
    });
  }, [openResolveModal]);

  // "Open" — jump to the job itself when the bell's summary isn't enough.
  // Same deep link the auto-schedule badge uses: the customer detail page
  // auto-opens the job drawer for ?dispatch_id.
  const openJob = useCallback((job: { job_id: number; customer_id: number | null }) => {
    if (!job.customer_id) return;
    setOpen(false);
    history.push(`/tenant/sales-crm/customer/detail/${job.customer_id}?dispatch_id=${job.job_id}`);
  }, []);

  // ----------------------------------------------------------------- orphan resolve
  const openOrphanChooser = useCallback((orphan: OrphanedAppointment) => {
    if (orphan.current_user_id) {
      setOrphanAction('assign_current');
    } else {
      setOrphanAction(canRemoveAppointment ? 'release' : 'readd_primary');
    }
    setOrphanRemoval({});
    setOrphanChooser(orphan);
  }, [canRemoveAppointment]);

  const submitOrphanAction = useCallback(async (
    orphan: OrphanedAppointment,
    action: OrphanResolveAction,
    times?: { time_from: string; time_to: string },
  ) => {
    setOrphanSubmitting(true);
    try {
      const res = await resolveOrphanAppointment({
        appointment_id: orphan.appointment_id,
        action,
        ...(times || {}),
        ...(action === 'release'
          ? {
              removal_reason: orphanRemoval.removal_reason,
              ...(orphanRemoval.removal_note?.trim() ? { removal_note: orphanRemoval.removal_note.trim() } : {}),
            }
          : {}),
      });
      if (res?.success === false) {
        message.error(res?.message || 'Failed to resolve the orphaned appointment');
        return false;
      }
      message.success('Orphaned appointment resolved');
      fetchConflicts();
      return true;
    } catch (e: any) {
      if (e?.response?.status === 409) {
        // Overlap on the technician's day (or schedule busy) — the global
        // handler already showed the server's message. Refresh so the bell
        // reflects current state; the dispatcher can pick another action.
        fetchConflicts();
      } else {
        message.error(e?.response?.data?.message || e?.message || 'Failed to resolve the orphaned appointment');
      }
      return false;
    } finally {
      setOrphanSubmitting(false);
    }
  }, [fetchConflicts, orphanRemoval]);

  const handleOrphanChooserOk = useCallback(async () => {
    if (!orphanChooser) return;
    const orphan = orphanChooser;

    if (orphanAction === 'assign_current') {
      // Slot choice needed: open the TimeSlotModal on the current tech's day;
      // the save routes through resolveOrphanAppointment (row-safe).
      if (!orphan.current_user_id) return;
      setOrphanChooser(null);
      openResolveModal(
        orphan.job_id,
        orphan.current_user_id,
        orphan.appointment_date,
        { startTime: orphan.time_from ?? undefined, endTime: orphan.time_to ?? undefined },
        orphan.appointment_id,
      );
      return;
    }

    const done = await submitOrphanAction(orphan, orphanAction);
    if (done) {
      setOrphanChooser(null);
    }
  }, [orphanChooser, orphanAction, openResolveModal, submitOrphanAction]);

  // ----------------------------------------------------------------- modal save
  const handleModalSave = useCallback(async (timeFrom: string, timeTo: string) => {
    if (!resolveTarget) return;
    const { jobId, userId, appointmentDate, orphanAppointmentId } = resolveTarget;

    // Orphan flow: row-safe endpoint handles re-pointing + duplicate cleanup.
    if (orphanAppointmentId) {
      const done = await submitOrphanAction(
        { appointment_id: orphanAppointmentId } as OrphanedAppointment,
        'assign_current',
        { time_from: timeFrom, time_to: timeTo },
      );
      if (done) {
        setResolveTarget(null);
      } else {
        // Reload the day so the modal shows current occupancy for a retry.
        try {
          const slots = await loadDaySlots(userId, appointmentDate);
          setResolveTarget((prev) => (prev ? { ...prev, slots } : prev));
        } catch {
          // keep the stale slots — the next save attempt is still validated server-side
        }
      }
      return;
    }

    try {
      const res = await updateDispatchAppt({
        job_id: String(jobId),
        user_id: String(userId),
        appointment_date: appointmentDate,
        time_from: timeFrom,
        time_to: timeTo,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Failed to update the appointment');
        return;
      }
      message.success('Appointment rescheduled');
      setResolveTarget(null);
      fetchConflicts();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        // Server rejected the slot (taken since the modal loaded, or the
        // schedule is being edited). The global request handler already
        // showed the server's message — reload the day so the modal shows
        // the current schedule for another attempt.
        try {
          const slots = await loadDaySlots(userId, appointmentDate);
          setResolveTarget((prev) => (prev ? { ...prev, slots } : prev));
        } catch {
          // keep the stale slots — the next save attempt is still validated server-side
        }
        fetchConflicts();
      } else {
        message.error(e?.message || 'Failed to update the appointment');
      }
    }
  }, [resolveTarget, fetchConflicts, loadDaySlots, submitOrphanAction]);

  // ----------------------------------------------------------------- render
  const content = (
    <div style={{ width: 460 }}>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text strong>Schedule Conflicts</Typography.Text>
      </div>

      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        <Spin spinning={loading}>
          {groups.length === 0 && orphans.length === 0 ? (
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 24 }}>
              {loading ? ' ' : 'No schedule conflicts'}
            </Typography.Text>
          ) : (
            <>
              {groups.map((group) => (
                <div key={`${group.user_id}|${group.appointment_date}`} style={{ marginBottom: 12 }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {group.technician_name || `Tech #${group.user_id}`} — {dayjs(group.appointment_date).format('M/D/YYYY')}
                  </Typography.Text>
                  <List
                    size="small"
                    dataSource={group.jobs}
                    renderItem={(job) => (
                      <List.Item
                        style={{ padding: '6px 4px', background: 'rgba(255,77,79,0.06)', borderRadius: 4, marginTop: 4 }}
                        actions={[
                          <Button
                            key="resolve"
                            size="small"
                            type="primary"
                            ghost
                            loading={resolvingJobId === job.job_id}
                            onClick={() => handleResolve(group, job)}
                          >
                            Resolve
                          </Button>,
                          ...(job.customer_id
                            ? [
                                <Button key="open" size="small" type="link" style={{ padding: 0 }} onClick={() => openJob(job)}>
                                  Open
                                </Button>,
                              ]
                            : []),
                        ]}
                      >
                        <List.Item.Meta
                          title={<Typography.Text style={{ fontSize: 13 }}>{job.customer_name || `Job #${job.job_id}`}</Typography.Text>}
                          description={
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              <div>
                                #{job.job_id} · {formatTimeAmPm(job.time_from)} – {formatTimeAmPm(job.time_to)}
                              </div>
                              <JobContextLines job={job} />
                            </Typography.Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              ))}

              {orphans.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    Orphaned appointments
                  </Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      The scheduled technician is no longer assigned to these jobs.
                    </Typography.Text>
                  </div>
                  <List
                    size="small"
                    dataSource={orphans}
                    renderItem={(orphan) => (
                      <List.Item
                        style={{ padding: '6px 4px', background: 'rgba(250,173,20,0.08)', borderRadius: 4, marginTop: 4 }}
                        actions={[
                          <Button
                            key="resolve"
                            size="small"
                            type="primary"
                            ghost
                            loading={resolvingJobId === orphan.job_id}
                            onClick={() => openOrphanChooser(orphan)}
                          >
                            Resolve
                          </Button>,
                          ...(orphan.customer_id
                            ? [
                                <Button key="open" size="small" type="link" style={{ padding: 0 }} onClick={() => openJob(orphan)}>
                                  Open
                                </Button>,
                              ]
                            : []),
                        ]}
                      >
                        <List.Item.Meta
                          title={<Typography.Text style={{ fontSize: 13 }}>{orphan.customer_name || `Job #${orphan.job_id}`}</Typography.Text>}
                          description={
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              <div>
                                #{orphan.job_id} · {dayjs(orphan.appointment_date).format('M/D')}
                                {orphan.time_from ? ` · ${formatTimeAmPm(orphan.time_from)} – ${formatTimeAmPm(orphan.time_to || orphan.time_from)}` : ''}
                              </div>
                              <JobContextLines job={orphan} />
                              <div>Slot held for: {orphan.phantom_technician_name || `Tech #${orphan.phantom_user_id}`} (removed)</div>
                              <div>Current tech: {orphan.current_technician_name || 'Unassigned'}</div>
                            </Typography.Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </>
          )}
        </Spin>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
      >
        <span className={`${styles.action}`} style={{ cursor: 'pointer' }}>
          <Tooltip title={conflictCount > 0 ? `${conflictCount} schedule conflicts need attention` : 'Schedule conflicts'}>
            <Badge count={conflictCount} overflowCount={99} size="small">
              <ExclamationCircleOutlined style={{ fontSize: 18, color: conflictCount > 0 ? '#ff7875' : '#fff' }} />
            </Badge>
          </Tooltip>
        </span>
      </Popover>

      {orphanChooser && (
        <Modal
          open
          title={`Resolve orphaned appointment — Job #${orphanChooser.job_id}`}
          okText="Apply"
          confirmLoading={orphanSubmitting}
          okButtonProps={{ disabled: orphanAction === 'release' && !isRemovalPayloadComplete(orphanRemoval) }}
          onOk={handleOrphanChooserOk}
          onCancel={() => setOrphanChooser(null)}
        >
          <div style={{ marginBottom: 12 }}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              {orphanChooser.customer_name || `Job #${orphanChooser.job_id}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              <JobContextLines job={orphanChooser} />
            </Typography.Text>
          </div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
            This slot ({orphanChooser.time_from
              ? `${formatTimeAmPm(orphanChooser.time_from)} – ${formatTimeAmPm(orphanChooser.time_to || orphanChooser.time_from)}`
              : 'no time set'} on {dayjs(orphanChooser.appointment_date).format('M/D/YYYY')}) is held
            for <b>{orphanChooser.phantom_technician_name || `Tech #${orphanChooser.phantom_user_id}`}</b>, who is no
            longer assigned to this job.
          </Typography.Paragraph>
          <Radio.Group
            value={orphanAction}
            onChange={(e) => setOrphanAction(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical">
              {orphanChooser.current_user_id && (
                <Radio value="assign_current">
                  Move the slot to the current technician
                  (<b>{orphanChooser.current_technician_name || `Tech #${orphanChooser.current_user_id}`}</b>) — pick a time next
                </Radio>
              )}
              <Radio value="readd_primary">
                Re-add <b>{orphanChooser.phantom_technician_name || `Tech #${orphanChooser.phantom_user_id}`}</b> as PRIMARY technician (keeps this slot)
              </Radio>
              <Radio value="readd_secondary">
                Re-add <b>{orphanChooser.phantom_technician_name || `Tech #${orphanChooser.phantom_user_id}`}</b> as secondary technician
              </Radio>
              <Radio value="release" disabled={!canRemoveAppointment}>
                Release the stale slot{orphanChooser.current_user_id ? '' : ' (job stays in the queue)'}
                {!canRemoveAppointment && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {' '}— requires the &quot;Remove Appointments&quot; permission
                  </Typography.Text>
                )}
              </Radio>
            </Space>
          </Radio.Group>
          {orphanAction === 'release' && canRemoveAppointment && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <AppointmentRemovalReasonPicker value={orphanRemoval} onChange={setOrphanRemoval} />
            </div>
          )}
        </Modal>
      )}

      {resolveTarget && (
        <TimeSlotModal
          visible
          onClose={() => setResolveTarget(null)}
          onSave={handleModalSave}
          appointmentDate={resolveTarget.appointmentDate}
          currentSlot={resolveTarget.currentSlot}
          dispatchId={resolveTarget.jobId}
          allTimeSlots={resolveTarget.slots}
          technicianUserId={resolveTarget.userId}
        />
      )}
    </>
  );
};

export default ConflictBell;
